import { ROUTES } from '@/lib/routes';
import type { ConfigureNavGroup } from './configure-nav';

/**
 * Remembers the last Configure section visited, scoped per organization.
 * Stores the SECTION-level path only (never a detail path): a visit to
 * `/orgs/acme/org/teams/t_1` remembers `/orgs/acme/org/teams`. Validated
 * against the current nav before restoring so a plugin uninstall,
 * permission revoke, or org switch falls back cleanly.
 *
 * localStorage (not sessionStorage): survives tab closes within the same
 * browser profile. try/catch mirrors takeover-return for private mode.
 */
const STORAGE_KEY_PREFIX = 'configure-section:';

function storageKey(slug: string): string {
  return `${STORAGE_KEY_PREFIX}${slug}`;
}

export function rememberConfigureSection(slug: string, sectionPath: string): void {
  if (!slug || !sectionPath) return;
  try {
    localStorage.setItem(storageKey(slug), sectionPath);
  } catch {
    /* storage unavailable — next open falls back to the default */
  }
}

export function readConfigureSection(slug: string): string | null {
  if (!slug) return null;
  try {
    return localStorage.getItem(storageKey(slug));
  } catch {
    return null;
  }
}

/**
 * Resolve the Configure door's landing path for `slug`. Returns the stored
 * section only when it still matches a row in `groups`; otherwise the first
 * row in group order; otherwise the Organization hub.
 */
export function resolveConfigureEntry(slug: string, groups: readonly ConfigureNavGroup[]): string {
  const fallback = slug ? ROUTES.org(slug).organizationHub : ROUTES.settings;
  const allItems = groups.flatMap((g) => g.items);
  if (allItems.length === 0) return fallback;

  const stored = readConfigureSection(slug);
  if (stored && allItems.some((i) => i.to === stored)) return stored;

  return allItems[0]?.to ?? fallback;
}
